/**
 * Seletor de emoji do LFRCORD: grade com categorias e busca por nome em
 * portugues. A lista e embutida aqui de proposito — o app nao ganha
 * dependencia nova por causa de emoji.
 *
 * O componente e independente do store: serve tanto para reagir a uma mensagem
 * quanto para inserir texto no compositor. Quem abre decide onde ele aparece
 * (o pai precisa ser `position: relative`) e o que fazer com a escolha.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { C, FONT_MONO } from "@/theme";

export type EmojiAnchor = "acima" | "abaixo";

export type EmojiPickerProps = {
  onPick: (emoji: string) => void;
  onClose: () => void;
  /** Lado em que a caixa abre em relacao ao botao. O padrao e `acima`. */
  anchor?: EmojiAnchor;
};

type CategoriaId =
  | "recentes"
  | "rostos"
  | "gestos"
  | "natureza"
  | "comida"
  | "atividades"
  | "viagem"
  | "objetos"
  | "simbolos";

type EmojiEntry = {
  char: string;
  /** Nome em portugues, mostrado no rodape e usado na busca. */
  name: string;
  /** Sinonimos que tambem levam ao emoji. */
  keys: string;
};

type Categoria = {
  id: Exclude<CategoriaId, "recentes">;
  label: string;
  /** Glifo da aba. */
  glyph: string;
  emojis: EmojiEntry[];
};

/** Atalho para a tabela abaixo ficar legivel. */
const e = (char: string, name: string, keys = ""): EmojiEntry => ({ char, name, keys });

const CATEGORIES: Categoria[] = [
  {
    id: "rostos",
    label: "Rostos e emoções",
    glyph: "😀",
    emojis: [
      e("😀", "rosto sorridente", "feliz sorriso alegre"),
      e("😃", "rosto sorridente com olhos grandes", "feliz alegre"),
      e("😄", "rosto risonho", "feliz alegria"),
      e("😁", "rosto radiante", "sorriso dentes"),
      e("😆", "rosto rindo", "risada gargalhada"),
      e("😅", "rosto rindo com suor", "alivio nervoso"),
      e("🤣", "rolando de rir", "risada gargalhada"),
      e("😂", "chorando de rir", "risada lagrimas"),
      e("🙂", "rosto levemente sorridente", "sorriso discreto"),
      e("🙃", "rosto de cabeça para baixo", "invertido ironia"),
      e("😉", "piscadinha", "piscar flerte"),
      e("😊", "rosto com olhos sorridentes", "fofo timido feliz"),
      e("😇", "rosto com auréola", "anjo inocente"),
      e("🥰", "rosto apaixonado", "coracoes amor"),
      e("😍", "olhos de coração", "amor paixao"),
      e("🤩", "estrelas nos olhos", "deslumbrado uau"),
      e("😘", "mandando beijo", "beijo amor"),
      e("😗", "rosto beijando", "beijo"),
      e("😚", "beijando com olhos fechados", "beijo"),
      e("😙", "beijando com olhos sorridentes", "beijo"),
      e("😋", "rosto saboreando", "delicioso gostoso"),
      e("😛", "mostrando a língua", "lingua brincadeira"),
      e("😜", "piscando com a língua de fora", "brincadeira zoeira"),
      e("🤪", "rosto maluco", "doido zoeira"),
      e("😝", "língua e olhos fechados", "brincadeira"),
      e("🤑", "rosto com cifrões", "dinheiro rico"),
      e("🤗", "rosto abraçando", "abraco carinho"),
      e("🤭", "mão na boca", "ops risada"),
      e("🤫", "pedindo silêncio", "shh segredo"),
      e("🤔", "rosto pensativo", "pensando duvida"),
      e("🤐", "boca fechada com zíper", "calado segredo"),
      e("🤨", "sobrancelha erguida", "desconfiado duvida"),
      e("😐", "rosto neutro", "indiferente"),
      e("😑", "rosto inexpressivo", "sem reacao"),
      e("😶", "rosto sem boca", "mudo"),
      e("😏", "rosto malicioso", "sarcasmo deboche"),
      e("😒", "rosto contrariado", "insatisfeito"),
      e("🙄", "revirando os olhos", "tedio deboche"),
      e("😬", "rosto constrangido", "vergonha nervoso"),
      e("🤥", "rosto de mentiroso", "mentira pinoquio"),
      e("😌", "rosto aliviado", "calma paz"),
      e("😔", "rosto cabisbaixo", "triste desanimado"),
      e("😪", "rosto sonolento", "sono cansado"),
      e("🤤", "rosto babando", "baba desejo"),
      e("😴", "rosto dormindo", "sono zzz"),
      e("😷", "rosto com máscara", "doente"),
      e("🤒", "rosto com termômetro", "febre doente"),
      e("🤕", "rosto com atadura", "machucado ferido"),
      e("🤢", "rosto enjoado", "nojo enjoo"),
      e("🤮", "rosto vomitando", "vomito nojo"),
      e("🤧", "rosto espirrando", "espirro resfriado"),
      e("🥵", "rosto com calor", "quente"),
      e("🥶", "rosto com frio", "congelando"),
      e("🥴", "rosto embriagado", "tonto bebado"),
      e("😵", "rosto atordoado", "tonto choque"),
      e("🤯", "cabeça explodindo", "choque surpresa"),
      e("🤠", "rosto de cowboy", "chapeu caubói"),
      e("🥳", "rosto de festa", "comemoracao aniversario"),
      e("😎", "rosto com óculos escuros", "estiloso legal"),
      e("🤓", "rosto de nerd", "estudioso"),
      e("🧐", "rosto com monóculo", "investigando curioso"),
      e("😕", "rosto confuso", "duvida"),
      e("😟", "rosto preocupado", "preocupacao"),
      e("🙁", "rosto levemente triste", "triste"),
      e("😮", "rosto boquiaberto", "surpresa uau"),
      e("😯", "rosto surpreso", "espanto"),
      e("😲", "rosto atônito", "choque surpresa"),
      e("😳", "rosto ruborizado", "vergonha corado"),
      e("🥺", "rosto suplicante", "pidao carinha"),
      e("😦", "rosto assustado", "boca aberta"),
      e("😧", "rosto angustiado", "angustia"),
      e("😨", "rosto amedrontado", "medo"),
      e("😰", "rosto ansioso com suor", "ansiedade medo"),
      e("😥", "rosto triste mas aliviado", "decepcao"),
      e("😢", "rosto chorando", "choro triste"),
      e("😭", "chorando muito", "choro tristeza"),
      e("😱", "gritando de medo", "pavor susto"),
      e("😖", "rosto perplexo", "frustracao"),
      e("😣", "rosto perseverante", "esforco"),
      e("😞", "rosto decepcionado", "decepcao tristeza"),
      e("😓", "rosto abatido com suor", "cansaco"),
      e("😩", "rosto exausto", "cansaco"),
      e("😫", "rosto cansado", "cansaco"),
      e("🥱", "rosto bocejando", "bocejo sono"),
      e("😤", "rosto bufando", "raiva orgulho"),
      e("😡", "rosto furioso", "raiva bravo"),
      e("😠", "rosto zangado", "raiva bravo"),
      e("🤬", "rosto xingando", "palavrao raiva"),
      e("😈", "diabinho sorridente", "travesso diabo"),
      e("👿", "diabinho zangado", "diabo raiva"),
      e("💀", "caveira", "morte"),
      e("☠️", "caveira e ossos", "perigo morte"),
      e("💩", "cocô", "bosta"),
      e("🤡", "palhaço", "palhacada circo"),
      e("👻", "fantasma", "assombracao halloween"),
      e("👽", "alienígena", "et espaco"),
      e("🤖", "robô", "bot maquina"),
      e("😺", "gato sorrindo", "gato"),
      e("😻", "gato apaixonado", "gato amor"),
      e("🙀", "gato assustado", "gato susto"),
      e("😿", "gato chorando", "gato triste"),
    ],
  },
  {
    id: "gestos",
    label: "Gestos e pessoas",
    glyph: "✋",
    emojis: [
      e("👋", "aceno", "tchau ola oi"),
      e("🤚", "mão levantada", "parar"),
      e("✋", "mão aberta", "parar cinco"),
      e("🖐️", "mão com dedos separados", "mao"),
      e("🖖", "saudação vulcana", "vulcano star trek"),
      e("👌", "sinal de ok", "certo joia"),
      e("🤌", "mão italiana", "dedos gesto"),
      e("🤏", "pouquinho", "pequeno"),
      e("✌️", "sinal de paz", "vitoria paz"),
      e("🤞", "dedos cruzados", "sorte torcendo"),
      e("🤟", "te amo em libras", "amor mao"),
      e("🤘", "sinal de rock", "rock chifre"),
      e("🤙", "me liga", "shaka ligar"),
      e("👈", "dedo apontando para a esquerda", "esquerda"),
      e("👉", "dedo apontando para a direita", "direita"),
      e("👆", "dedo apontando para cima", "cima"),
      e("👇", "dedo apontando para baixo", "baixo"),
      e("☝️", "dedo indicador para cima", "atencao um"),
      e("👍", "joinha", "curtir positivo aprovado like"),
      e("👎", "negativo", "descurtir reprovado dislike"),
      e("✊", "punho erguido", "forca luta"),
      e("👊", "soco", "punho toca aqui"),
      e("🤛", "punho para a esquerda", "toca aqui"),
      e("🤜", "punho para a direita", "toca aqui"),
      e("👏", "palmas", "aplausos parabens"),
      e("🙌", "mãos comemorando", "comemoracao aleluia"),
      e("👐", "mãos abertas", "abraco"),
      e("🤲", "mãos em concha", "pedido oracao"),
      e("🤝", "aperto de mãos", "acordo negocio"),
      e("🙏", "mãos em oração", "obrigado por favor reza"),
      e("✍️", "mão escrevendo", "escrever"),
      e("💅", "esmalte", "unhas"),
      e("💪", "bíceps", "forca musculo academia"),
      e("🦾", "braço mecânico", "protese forca"),
      e("🧠", "cérebro", "mente ideia"),
      e("👀", "olhos", "olhando atencao"),
      e("👁️", "olho", "visao"),
      e("👄", "boca", "labios"),
      e("👅", "língua", "lamber"),
      e("🦷", "dente", "dentista"),
      e("🦴", "osso", "esqueleto"),
      e("👶", "bebê", "crianca"),
      e("🧒", "criança", "infantil"),
      e("👦", "menino", "garoto"),
      e("👧", "menina", "garota"),
      e("🧑", "pessoa", "adulto"),
      e("👨", "homem", "cara"),
      e("👩", "mulher", "moca"),
      e("🧔", "pessoa de barba", "barba"),
      e("👴", "senhor", "idoso vovo"),
      e("👵", "senhora", "idosa vovo"),
      e("🙇", "pessoa fazendo reverência", "desculpa respeito"),
      e("🤦", "mão na testa", "facepalm decepcao"),
      e("🤷", "dando de ombros", "sei la duvida"),
      e("🙋", "levantando a mão", "pergunta presente"),
      e("🙅", "fazendo sinal de não", "recusa nao"),
      e("🙆", "fazendo sinal de ok", "certo"),
      e("💁", "dando informação", "atendimento"),
      e("🧏", "pessoa surda", "libras"),
      e("🤵", "pessoa de smoking", "festa formal"),
      e("👮", "policial", "policia"),
      e("🕵️", "detetive", "investigador espiao"),
      e("👷", "trabalhador de obra", "construcao capacete"),
      e("💂", "guarda", "soldado"),
      e("🧑‍💻", "pessoa no computador", "programador dev"),
      e("🧑‍🚀", "astronauta", "espaco"),
      e("🦸", "super-herói", "heroi"),
      e("🦹", "supervilão", "vilao"),
      e("🧙", "mago", "magia feiticeiro"),
      e("🧛", "vampiro", "dracula"),
      e("🧟", "zumbi", "morto vivo"),
      e("🧜", "sereia", "mar"),
      e("🧚", "fada", "magia"),
      e("👼", "anjinho", "anjo bebe"),
      e("🎅", "papai noel", "natal"),
      e("🤶", "mamãe noel", "natal"),
      e("🕺", "homem dançando", "danca festa"),
      e("💃", "mulher dançando", "danca festa"),
      e("👯", "pessoas com orelhas de coelho", "festa dupla"),
      e("🧖", "pessoa na sauna", "spa relaxar"),
      e("🧘", "pessoa meditando", "ioga meditacao"),
      e("🛀", "pessoa no banho", "banheira"),
      e("🛌", "pessoa na cama", "dormir"),
      e("🤺", "esgrimista", "esgrima"),
      e("🏇", "jóquei", "cavalo corrida"),
      e("⛷️", "esquiador", "esqui neve"),
      e("🏂", "snowboarder", "neve"),
      e("🏄", "surfista", "surfe onda"),
      e("🚣", "pessoa remando", "remo barco"),
      e("🏊", "nadador", "natacao"),
      e("⛹️", "pessoa quicando bola", "basquete"),
      e("🏋️", "levantando peso", "academia musculacao"),
      e("🚴", "ciclista", "bicicleta"),
      e("🚵", "ciclista de montanha", "mountain bike"),
      e("🤸", "pessoa dando estrela", "ginastica"),
      e("🤼", "pessoas lutando", "luta"),
      e("🤽", "polo aquático", "piscina"),
      e("🤾", "handebol", "esporte"),
      e("🤹", "malabarista", "malabares circo"),
      e("👫", "casal de mãos dadas", "casal"),
      e("👨‍👩‍👧", "família", "pais filhos"),
      e("🗣️", "cabeça falando", "falando voz"),
      e("👤", "silhueta", "perfil usuario"),
      e("👥", "silhuetas", "grupo usuarios"),
    ],
  },
  {
    id: "natureza",
    label: "Animais e natureza",
    glyph: "🐻",
    emojis: [
      e("🐶", "cachorro", "cao dog"),
      e("🐱", "gato", "gatinho"),
      e("🐭", "rato", "camundongo"),
      e("🐹", "hamster", "roedor"),
      e("🐰", "coelho", "coelhinho"),
      e("🦊", "raposa", "fox"),
      e("🐻", "urso", "ursinho"),
      e("🐼", "panda", "urso"),
      e("🐨", "coala", "koala"),
      e("🐯", "tigre", "felino"),
      e("🦁", "leão", "felino"),
      e("🐮", "vaca", "boi"),
      e("🐷", "porco", "leitao"),
      e("🐸", "sapo", "ra"),
      e("🐵", "macaco", "simio"),
      e("🙈", "macaco que não vê", "ver vergonha"),
      e("🙉", "macaco que não ouve", "ouvir"),
      e("🙊", "macaco que não fala", "falar segredo"),
      e("🐔", "galinha", "ave"),
      e("🐧", "pinguim", "ave gelo"),
      e("🐦", "pássaro", "ave"),
      e("🐤", "pintinho", "filhote"),
      e("🦆", "pato", "ave"),
      e("🦅", "águia", "ave rapina"),
      e("🦉", "coruja", "ave noite"),
      e("🦇", "morcego", "noite"),
      e("🐺", "lobo", "selvagem"),
      e("🐗", "javali", "porco selvagem"),
      e("🐴", "cavalo", "egua"),
      e("🦄", "unicórnio", "fantasia"),
      e("🐝", "abelha", "mel"),
      e("🐛", "lagarta", "inseto"),
      e("🦋", "borboleta", "inseto"),
      e("🐌", "caracol", "lesma"),
      e("🐞", "joaninha", "inseto"),
      e("🐜", "formiga", "inseto"),
      e("🦗", "grilo", "inseto"),
      e("🕷️", "aranha", "inseto teia"),
      e("🦂", "escorpião", "veneno"),
      e("🐢", "tartaruga", "lento"),
      e("🐍", "cobra", "serpente"),
      e("🦎", "lagarto", "reptil"),
      e("🦖", "tiranossauro", "dinossauro"),
      e("🦕", "dinossauro", "pescoco longo"),
      e("🐙", "polvo", "mar"),
      e("🦑", "lula", "mar"),
      e("🦐", "camarão", "frutos do mar"),
      e("🦀", "caranguejo", "siri"),
      e("🐡", "baiacu", "peixe"),
      e("🐠", "peixe tropical", "peixe"),
      e("🐟", "peixe", "pescar"),
      e("🐬", "golfinho", "mar"),
      e("🐳", "baleia", "mar"),
      e("🦈", "tubarão", "mar"),
      e("🐊", "crocodilo", "jacare"),
      e("🐅", "tigre de corpo inteiro", "felino"),
      e("🦓", "zebra", "listras"),
      e("🦍", "gorila", "macaco"),
      e("🐘", "elefante", "tromba"),
      e("🦏", "rinoceronte", "chifre"),
      e("🐪", "camelo", "deserto"),
      e("🦒", "girafa", "pescoco"),
      e("🐃", "búfalo", "boi"),
      e("🐑", "ovelha", "carneiro"),
      e("🐐", "cabra", "bode"),
      e("🦌", "cervo", "veado"),
      e("🐕", "cão", "cachorro"),
      e("🐈", "gato doméstico", "felino"),
      e("🐓", "galo", "ave"),
      e("🦃", "peru", "ave"),
      e("🕊️", "pomba", "paz ave"),
      e("🐇", "coelho de corpo inteiro", "coelho"),
      e("🐁", "camundongo", "rato"),
      e("🐿️", "esquilo", "roedor"),
      e("🦔", "ouriço", "espinhos"),
      e("🐾", "patinhas", "pegadas"),
      e("🌵", "cacto", "deserto planta"),
      e("🌲", "pinheiro", "arvore"),
      e("🌳", "árvore", "natureza"),
      e("🌴", "palmeira", "praia"),
      e("🌱", "muda", "broto planta"),
      e("🌿", "erva", "folha planta"),
      e("☘️", "trevo", "sorte"),
      e("🍀", "trevo de quatro folhas", "sorte"),
      e("🍃", "folhas ao vento", "folha"),
      e("🍂", "folhas caídas", "outono"),
      e("🍁", "folha de bordo", "outono"),
      e("🌸", "flor de cerejeira", "flor"),
      e("🌺", "hibisco", "flor"),
      e("🌻", "girassol", "flor"),
      e("🌹", "rosa", "flor amor"),
      e("🌷", "tulipa", "flor"),
      e("🌼", "margarida", "flor"),
      e("💐", "buquê", "flores"),
      e("🍄", "cogumelo", "fungo"),
      e("🌎", "planeta terra", "mundo globo"),
      e("🌕", "lua cheia", "lua"),
      e("🌙", "lua crescente", "noite"),
      e("⭐", "estrela", "favorito"),
      e("🌟", "estrela brilhante", "brilho"),
      e("✨", "brilhos", "magia estrelas"),
      e("⚡", "raio", "energia trovao"),
      e("🔥", "fogo", "chama quente top"),
      e("💧", "gota", "agua"),
      e("🌊", "onda", "mar agua"),
      e("☀️", "sol", "quente verao"),
      e("⛅", "sol entre nuvens", "tempo"),
      e("☁️", "nuvem", "tempo"),
      e("🌧️", "nuvem com chuva", "chuva"),
      e("⛈️", "tempestade", "trovoada"),
      e("❄️", "floco de neve", "neve frio"),
      e("⛄", "boneco de neve", "neve"),
      e("🌈", "arco-íris", "cores"),
      e("🌪️", "tornado", "vento"),
    ],
  },
  {
    id: "comida",
    label: "Comida e bebida",
    glyph: "🍔",
    emojis: [
      e("🍏", "maçã verde", "fruta"),
      e("🍎", "maçã", "fruta"),
      e("🍐", "pera", "fruta"),
      e("🍊", "laranja", "fruta"),
      e("🍋", "limão", "fruta"),
      e("🍌", "banana", "fruta"),
      e("🍉", "melancia", "fruta"),
      e("🍇", "uvas", "fruta"),
      e("🍓", "morango", "fruta"),
      e("🫐", "mirtilo", "fruta"),
      e("🍈", "melão", "fruta"),
      e("🍒", "cerejas", "fruta"),
      e("🍑", "pêssego", "fruta"),
      e("🥭", "manga", "fruta"),
      e("🍍", "abacaxi", "fruta"),
      e("🥥", "coco", "fruta"),
      e("🥝", "kiwi", "fruta"),
      e("🍅", "tomate", "legume"),
      e("🥑", "abacate", "fruta"),
      e("🍆", "berinjela", "legume"),
      e("🥔", "batata", "legume"),
      e("🥕", "cenoura", "legume"),
      e("🌽", "milho", "espiga"),
      e("🌶️", "pimenta", "apimentado"),
      e("🥒", "pepino", "legume"),
      e("🥬", "folhas verdes", "salada"),
      e("🥦", "brócolis", "legume"),
      e("🧄", "alho", "tempero"),
      e("🧅", "cebola", "tempero"),
      e("🍞", "pão", "padaria"),
      e("🥐", "croissant", "padaria"),
      e("🥖", "baguete", "pao"),
      e("🧀", "queijo", "laticinio"),
      e("🥚", "ovo", "cafe da manha"),
      e("🍳", "ovo frito", "cafe da manha"),
      e("🥞", "panquecas", "cafe da manha"),
      e("🧇", "waffle", "cafe da manha"),
      e("🥓", "bacon", "carne"),
      e("🥩", "carne", "bife churrasco"),
      e("🍗", "coxa de frango", "frango"),
      e("🍖", "carne com osso", "churrasco"),
      e("🌭", "cachorro-quente", "hot dog lanche"),
      e("🍔", "hambúrguer", "lanche"),
      e("🍟", "batatas fritas", "lanche"),
      e("🍕", "pizza", "lanche"),
      e("🥪", "sanduíche", "lanche"),
      e("🌮", "taco", "mexicano"),
      e("🌯", "burrito", "mexicano"),
      e("🥗", "salada", "saudavel"),
      e("🍝", "macarrão", "massa espaguete"),
      e("🍜", "macarrão em tigela", "lamen sopa japonesa"),
      e("🍲", "panela de comida", "ensopado"),
      e("🍛", "curry com arroz", "arroz"),
      e("🍣", "sushi", "japonesa"),
      e("🍱", "marmita japonesa", "bento"),
      e("🥟", "guioza", "massa recheada"),
      e("🍤", "camarão empanado", "fritura"),
      e("🍚", "tigela de arroz", "arroz"),
      e("🍙", "bolinho de arroz", "onigiri"),
      e("🥠", "biscoito da sorte", "sorte"),
      e("🍦", "sorvete de casquinha", "sobremesa"),
      e("🍧", "raspadinha", "gelo sobremesa"),
      e("🍨", "sorvete", "sobremesa"),
      e("🍩", "rosquinha", "donut doce"),
      e("🍪", "biscoito", "cookie doce"),
      e("🎂", "bolo de aniversário", "festa parabens"),
      e("🍰", "fatia de bolo", "sobremesa"),
      e("🧁", "cupcake", "doce"),
      e("🥧", "torta", "doce"),
      e("🍫", "chocolate", "doce"),
      e("🍬", "bala", "doce"),
      e("🍭", "pirulito", "doce"),
      e("🍯", "mel", "doce"),
      e("🍼", "mamadeira", "bebe"),
      e("🥛", "leite", "bebida"),
      e("☕", "café", "bebida quente cafezinho"),
      e("🍵", "chá", "bebida"),
      e("🧃", "suco", "bebida caixinha"),
      e("🥤", "copo com canudo", "refrigerante"),
      e("🧋", "chá de bolhas", "bubble tea"),
      e("🍺", "chope", "cerveja"),
      e("🍻", "canecas de cerveja", "brinde"),
      e("🍷", "vinho", "taca"),
      e("🥂", "taças de brinde", "comemoracao"),
      e("🍸", "coquetel", "drinque"),
      e("🍹", "drinque tropical", "bebida"),
      e("🥃", "uísque", "copo"),
      e("🍾", "champanhe", "comemoracao"),
      e("🧉", "chimarrão", "mate erva"),
      e("🧊", "gelo", "cubo"),
      e("🍽️", "prato e talheres", "jantar"),
      e("🥄", "colher", "talher"),
      e("🍴", "garfo e faca", "talher"),
    ],
  },
  {
    id: "atividades",
    label: "Atividades",
    glyph: "⚽",
    emojis: [
      e("⚽", "bola de futebol", "futebol"),
      e("🏀", "bola de basquete", "basquete"),
      e("🏈", "futebol americano", "bola"),
      e("⚾", "beisebol", "bola"),
      e("🎾", "tênis", "bola raquete"),
      e("🏐", "vôlei", "bola"),
      e("🏉", "rúgbi", "bola"),
      e("🥏", "frisbee", "disco"),
      e("🎱", "bola oito", "sinuca"),
      e("🏓", "pingue-pongue", "tenis de mesa"),
      e("🏸", "badminton", "peteca"),
      e("🥅", "gol", "trave rede"),
      e("🏒", "hóquei no gelo", "taco"),
      e("🏑", "hóquei na grama", "taco"),
      e("🏏", "críquete", "taco"),
      e("🥊", "luva de boxe", "boxe luta"),
      e("🥋", "quimono", "artes marciais judo"),
      e("⛳", "golfe", "bandeira buraco"),
      e("🎣", "pesca", "vara peixe"),
      e("🤿", "mergulho", "mascara"),
      e("🎽", "camiseta de corrida", "atletismo"),
      e("🎿", "esqui", "neve"),
      e("🛷", "trenó", "neve"),
      e("🎯", "alvo", "dardo mira"),
      e("🎮", "controle de videogame", "jogo game"),
      e("🕹️", "joystick", "arcade jogo"),
      e("🎲", "dado", "sorte jogo"),
      e("♟️", "peão de xadrez", "xadrez"),
      e("🧩", "quebra-cabeça", "peca puzzle"),
      e("🎰", "caça-níqueis", "cassino"),
      e("🎳", "boliche", "pinos"),
      e("🎨", "paleta de tintas", "arte pintura"),
      e("🎭", "máscaras de teatro", "teatro drama"),
      e("🎤", "microfone", "cantar karaoke"),
      e("🎧", "fone de ouvido", "musica"),
      e("🎼", "partitura", "musica"),
      e("🎵", "nota musical", "musica"),
      e("🎶", "notas musicais", "musica"),
      e("🎸", "guitarra", "violao musica"),
      e("🎹", "teclado musical", "piano"),
      e("🥁", "bateria", "tambor"),
      e("🎺", "trompete", "musica"),
      e("🎻", "violino", "musica"),
      e("🪕", "banjo", "musica"),
      e("🎬", "claquete", "cinema filme"),
      e("🎥", "câmera de cinema", "filmagem"),
      e("📷", "câmera", "foto"),
      e("📸", "câmera com flash", "foto"),
      e("🏆", "troféu", "vitoria campeao"),
      e("🥇", "medalha de ouro", "primeiro lugar"),
      e("🥈", "medalha de prata", "segundo lugar"),
      e("🥉", "medalha de bronze", "terceiro lugar"),
      e("🏅", "medalha", "premio"),
      e("🎖️", "medalha militar", "honra"),
      e("🎗️", "laço", "apoio campanha"),
      e("🎫", "ingresso", "entrada"),
      e("🎟️", "ingressos", "entrada evento"),
      e("🎪", "circo", "tenda"),
      e("🎡", "roda-gigante", "parque"),
      e("🎢", "montanha-russa", "parque"),
      e("🎠", "carrossel", "parque"),
      e("🎉", "festa", "confete comemoracao"),
      e("🎊", "confete", "festa"),
      e("🎈", "balão", "festa"),
      e("🎁", "presente", "aniversario"),
      e("🎀", "laço de fita", "presente"),
      e("🧨", "fogos de artifício", "explosivo"),
      e("🎆", "fogos no céu", "festa"),
      e("🎇", "faísca", "fogos"),
    ],
  },
  {
    id: "viagem",
    label: "Viagem e lugares",
    glyph: "🚗",
    emojis: [
      e("🚗", "carro", "automovel"),
      e("🚕", "táxi", "carro"),
      e("🚙", "utilitário", "carro suv"),
      e("🚌", "ônibus", "coletivo"),
      e("🏎️", "carro de corrida", "formula"),
      e("🚓", "viatura", "policia carro"),
      e("🚑", "ambulância", "emergencia"),
      e("🚒", "caminhão de bombeiros", "bombeiro"),
      e("🚐", "van", "furgao"),
      e("🚚", "caminhão", "carga"),
      e("🚛", "carreta", "caminhao carga"),
      e("🚜", "trator", "fazenda"),
      e("🛵", "lambreta", "scooter moto"),
      e("🏍️", "motocicleta", "moto"),
      e("🚲", "bicicleta", "bike"),
      e("🛴", "patinete", "scooter"),
      e("🛹", "skate", "manobra"),
      e("🚨", "sirene", "policia alerta"),
      e("🚄", "trem-bala", "trem"),
      e("🚆", "trem", "ferrovia"),
      e("🚇", "metrô", "trem"),
      e("🚊", "bonde", "trem"),
      e("🚂", "locomotiva", "trem vapor"),
      e("✈️", "avião", "voo viagem"),
      e("🛫", "decolagem", "aviao"),
      e("🛬", "pouso", "aviao"),
      e("🚁", "helicóptero", "voo"),
      e("🚀", "foguete", "espaco lancamento"),
      e("🛸", "disco voador", "ovni"),
      e("⛵", "veleiro", "barco"),
      e("🚤", "lancha", "barco"),
      e("🛳️", "navio de passageiros", "cruzeiro"),
      e("⛴️", "balsa", "barco"),
      e("🚢", "navio", "barco"),
      e("⚓", "âncora", "navio"),
      e("🗺️", "mapa-múndi", "mapa viagem"),
      e("🧭", "bússola", "direcao"),
      e("🏔️", "montanha com neve", "montanha"),
      e("⛰️", "montanha", "natureza"),
      e("🌋", "vulcão", "lava"),
      e("🏕️", "acampamento", "barraca"),
      e("🏖️", "praia", "guarda-sol"),
      e("🏜️", "deserto", "areia"),
      e("🏝️", "ilha deserta", "praia"),
      e("🗻", "monte fuji", "montanha"),
      e("🏙️", "cidade", "predios"),
      e("🌃", "noite estrelada", "cidade noite"),
      e("🌆", "cidade ao entardecer", "por do sol"),
      e("🌇", "pôr do sol na cidade", "entardecer"),
      e("🌉", "ponte à noite", "ponte"),
      e("🏠", "casa", "lar"),
      e("🏡", "casa com jardim", "lar"),
      e("🏢", "prédio", "escritorio"),
      e("🏥", "hospital", "saude"),
      e("🏦", "banco", "dinheiro"),
      e("🏨", "hotel", "hospedagem"),
      e("🏫", "escola", "estudo"),
      e("🏭", "fábrica", "industria"),
      e("🏛️", "prédio clássico", "museu"),
      e("⛪", "igreja", "religiao"),
      e("🕌", "mesquita", "religiao"),
      e("🛕", "templo hindu", "religiao"),
      e("🕍", "sinagoga", "religiao"),
      e("⛺", "barraca", "acampamento"),
      e("🗼", "torre de tóquio", "torre"),
      e("🗽", "estátua da liberdade", "nova york"),
      e("🚏", "ponto de ônibus", "parada"),
      e("🚦", "semáforo", "transito"),
      e("🚧", "obras", "construcao"),
      e("🛑", "sinal de pare", "parar"),
      e("⛽", "posto de combustível", "gasolina"),
    ],
  },
  {
    id: "objetos",
    label: "Objetos",
    glyph: "💡",
    emojis: [
      e("⌚", "relógio de pulso", "hora"),
      e("📱", "celular", "telefone smartphone"),
      e("💻", "notebook", "computador laptop"),
      e("🖥️", "computador de mesa", "monitor"),
      e("🖨️", "impressora", "imprimir"),
      e("⌨️", "teclado", "digitar"),
      e("🖱️", "mouse", "computador"),
      e("💾", "disquete", "salvar"),
      e("💿", "cd", "midia disco"),
      e("📀", "dvd", "midia"),
      e("📞", "telefone", "ligacao"),
      e("☎️", "telefone fixo", "ligacao"),
      e("📠", "fax", "maquina"),
      e("📺", "televisão", "tv"),
      e("📻", "rádio", "som"),
      e("🎙️", "microfone de estúdio", "podcast"),
      e("🎚️", "controle deslizante", "mixagem"),
      e("🎛️", "botões de controle", "mixagem"),
      e("⏱️", "cronômetro", "tempo"),
      e("⏰", "despertador", "alarme hora"),
      e("⏳", "ampulheta", "tempo espera"),
      e("🔋", "bateria", "energia"),
      e("🔌", "tomada", "energia plugue"),
      e("💡", "lâmpada", "ideia luz"),
      e("🔦", "lanterna", "luz"),
      e("🕯️", "vela", "luz"),
      e("🧯", "extintor", "incendio"),
      e("🛢️", "barril", "petroleo"),
      e("💸", "dinheiro voando", "gasto"),
      e("💵", "nota de dinheiro", "dolar"),
      e("💰", "saco de dinheiro", "grana"),
      e("💳", "cartão de crédito", "pagamento"),
      e("🧾", "recibo", "nota fiscal"),
      e("💎", "diamante", "joia"),
      e("⚖️", "balança", "justica"),
      e("🧰", "caixa de ferramentas", "reparo"),
      e("🔧", "chave inglesa", "ferramenta"),
      e("🔨", "martelo", "ferramenta"),
      e("🛠️", "martelo e chave", "ferramentas"),
      e("⛏️", "picareta", "mineracao"),
      e("🔩", "parafuso e porca", "peca"),
      e("⚙️", "engrenagem", "configuracao ajuste"),
      e("🧲", "ímã", "magnetico"),
      e("💣", "bomba", "explosivo"),
      e("🔪", "faca", "cozinha"),
      e("🗡️", "espada", "lamina"),
      e("🛡️", "escudo", "protecao"),
      e("🔮", "bola de cristal", "magia futuro"),
      e("🧿", "olho grego", "protecao amuleto"),
      e("🧪", "tubo de ensaio", "quimica"),
      e("🧫", "placa de petri", "biologia"),
      e("🧬", "dna", "genetica"),
      e("🔬", "microscópio", "ciencia"),
      e("🔭", "telescópio", "astronomia"),
      e("📡", "antena parabólica", "sinal"),
      e("💊", "comprimido", "remedio"),
      e("💉", "seringa", "vacina injecao"),
      e("🩹", "curativo", "machucado"),
      e("🩺", "estetoscópio", "medico"),
      e("🚪", "porta", "entrada"),
      e("🪑", "cadeira", "assento"),
      e("🛏️", "cama", "dormir"),
      e("🚿", "chuveiro", "banho"),
      e("🛁", "banheira", "banho"),
      e("🧻", "papel higiênico", "banheiro"),
      e("🧼", "sabonete", "limpeza"),
      e("🧹", "vassoura", "limpeza"),
      e("🧺", "cesto", "roupa"),
      e("🔑", "chave", "acesso"),
      e("🗝️", "chave antiga", "segredo"),
      e("🔒", "cadeado fechado", "seguranca"),
      e("🔓", "cadeado aberto", "desbloqueado"),
      e("🔔", "sino", "notificacao"),
      e("🔕", "sino cortado", "silenciar"),
      e("📣", "megafone", "anuncio"),
      e("📢", "alto-falante", "aviso"),
      e("📌", "alfinete", "fixar"),
      e("📍", "marcador de local", "localizacao"),
      e("📎", "clipe de papel", "anexo"),
      e("✂️", "tesoura", "cortar"),
      e("📏", "régua", "medir"),
      e("🗑️", "lixeira", "apagar"),
      e("📦", "caixa", "pacote entrega"),
      e("📫", "caixa de correio", "correspondencia"),
      e("✉️", "envelope", "email carta"),
      e("📧", "email", "mensagem"),
      e("📅", "calendário", "data"),
      e("📋", "prancheta", "lista"),
      e("📁", "pasta", "arquivo"),
      e("📂", "pasta aberta", "arquivo"),
      e("📄", "documento", "pagina"),
      e("📊", "gráfico de barras", "dados"),
      e("📈", "gráfico crescente", "alta subindo"),
      e("📉", "gráfico decrescente", "queda caindo"),
      e("📝", "anotação", "escrever nota"),
      e("✏️", "lápis", "escrever"),
      e("🖊️", "caneta", "escrever"),
      e("🖌️", "pincel", "pintar"),
      e("🖍️", "giz de cera", "desenhar"),
      e("📚", "livros", "estudo"),
      e("📖", "livro aberto", "leitura"),
      e("🔖", "marcador de página", "favorito"),
      e("🏷️", "etiqueta", "tag"),
      e("🔍", "lupa", "buscar procurar"),
      e("🔎", "lupa à direita", "buscar"),
      e("🧳", "mala", "viagem"),
      e("🎒", "mochila", "escola"),
      e("👕", "camiseta", "roupa"),
      e("👖", "calça jeans", "roupa"),
      e("👟", "tênis", "calcado"),
      e("🧢", "boné", "chapeu"),
      e("👑", "coroa", "rei rainha"),
      e("💍", "anel", "casamento"),
      e("👓", "óculos", "visao"),
      e("🕶️", "óculos escuros", "sol"),
      e("🥽", "óculos de proteção", "seguranca"),
    ],
  },
  {
    id: "simbolos",
    label: "Símbolos",
    glyph: "❤️",
    emojis: [
      e("❤️", "coração vermelho", "amor"),
      e("🧡", "coração laranja", "amor"),
      e("💛", "coração amarelo", "amor"),
      e("💚", "coração verde", "amor"),
      e("💙", "coração azul", "amor"),
      e("💜", "coração roxo", "amor"),
      e("🖤", "coração preto", "amor"),
      e("🤍", "coração branco", "amor"),
      e("🤎", "coração marrom", "amor"),
      e("💔", "coração partido", "termino tristeza"),
      e("❣️", "exclamação de coração", "amor"),
      e("💕", "dois corações", "amor"),
      e("💞", "corações girando", "amor"),
      e("💓", "coração pulsante", "amor"),
      e("💗", "coração crescendo", "amor"),
      e("💖", "coração brilhante", "amor"),
      e("💘", "coração com flecha", "paixao"),
      e("💝", "coração com laço", "presente"),
      e("💯", "cem pontos", "perfeito nota"),
      e("💢", "símbolo de raiva", "irritacao"),
      e("💥", "explosão", "colisao"),
      e("💫", "tontura", "estrelas"),
      e("💦", "gotas de suor", "agua"),
      e("💨", "rajada de vento", "correndo"),
      e("🕳️", "buraco", "vazio"),
      e("💬", "balão de fala", "mensagem"),
      e("💭", "balão de pensamento", "pensar"),
      e("🗯️", "balão de raiva", "grito"),
      e("🔊", "alto-falante ligado", "som volume"),
      e("🔉", "volume médio", "som"),
      e("🔈", "volume baixo", "som"),
      e("🔇", "mudo", "som desligado"),
      e("✅", "marca de seleção", "certo feito"),
      e("☑️", "caixa marcada", "feito"),
      e("✔️", "certo", "confirmado"),
      e("❌", "errado", "cancelar"),
      e("❎", "x em caixa", "errado"),
      e("➕", "mais", "somar"),
      e("➖", "menos", "subtrair"),
      e("➗", "divisão", "dividir"),
      e("✖️", "multiplicação", "vezes"),
      e("♾️", "infinito", "sem fim"),
      e("❓", "ponto de interrogação", "duvida"),
      e("❗", "ponto de exclamação", "atencao"),
      e("⚠️", "aviso", "atencao perigo"),
      e("🚫", "proibido", "bloqueado"),
      e("⛔", "entrada proibida", "bloqueado"),
      e("🔞", "proibido para menores", "dezoito"),
      e("📵", "celular proibido", "sem telefone"),
      e("♻️", "reciclagem", "reciclar"),
      e("🔰", "símbolo de iniciante", "novato"),
      e("🆗", "ok", "certo"),
      e("🆕", "novo", "novidade"),
      e("🆒", "legal", "cool"),
      e("🆙", "para cima", "subir"),
      e("🆓", "grátis", "livre"),
      e("🔝", "topo", "para cima"),
      e("🔙", "voltar", "retorno"),
      e("🔜", "em breve", "logo"),
      e("🔄", "setas girando", "atualizar"),
      e("🔁", "repetir", "loop"),
      e("🔂", "repetir uma vez", "loop"),
      e("▶️", "reproduzir", "play"),
      e("⏸️", "pausar", "pause"),
      e("⏹️", "parar", "stop"),
      e("⏺️", "gravar", "rec"),
      e("⏭️", "próxima faixa", "avancar"),
      e("⏮️", "faixa anterior", "voltar"),
      e("⏩", "avanço rápido", "adiantar"),
      e("⏪", "retrocesso rápido", "voltar"),
      e("🔀", "aleatório", "embaralhar"),
      e("🔺", "triângulo vermelho para cima", "alta"),
      e("🔻", "triângulo vermelho para baixo", "baixa"),
      e("🔴", "círculo vermelho", "gravando"),
      e("🟠", "círculo laranja", "cor"),
      e("🟡", "círculo amarelo", "cor"),
      e("🟢", "círculo verde", "online"),
      e("🔵", "círculo azul", "cor"),
      e("🟣", "círculo roxo", "cor"),
      e("⚫", "círculo preto", "cor"),
      e("⚪", "círculo branco", "cor"),
      e("🟥", "quadrado vermelho", "cor"),
      e("🟧", "quadrado laranja", "cor"),
      e("🟨", "quadrado amarelo", "cor"),
      e("🟩", "quadrado verde", "cor"),
      e("🟦", "quadrado azul", "cor"),
      e("⬛", "quadrado preto", "cor"),
      e("⬜", "quadrado branco", "cor"),
      e("♠️", "espadas", "baralho"),
      e("♥️", "copas", "baralho"),
      e("♦️", "ouros", "baralho"),
      e("♣️", "paus", "baralho"),
      e("🃏", "curinga", "baralho"),
      e("🎴", "cartas de flores", "jogo"),
      e("📛", "crachá", "nome"),
      e("🔱", "tridente", "simbolo"),
      e("☢️", "radioativo", "perigo"),
      e("☣️", "risco biológico", "perigo"),
      e("✝️", "cruz latina", "religiao"),
      e("☪️", "estrela e crescente", "religiao"),
      e("✡️", "estrela de davi", "religiao"),
      e("☮️", "símbolo da paz", "paz"),
      e("☯️", "yin yang", "equilibrio"),
      e("🕉️", "om", "religiao"),
      e("♈", "signo de áries", "zodiaco"),
      e("♉", "signo de touro", "zodiaco"),
      e("♊", "signo de gêmeos", "zodiaco"),
      e("♋", "signo de câncer", "zodiaco"),
      e("♌", "signo de leão", "zodiaco"),
      e("♍", "signo de virgem", "zodiaco"),
      e("♎", "signo de libra", "zodiaco"),
      e("♏", "signo de escorpião", "zodiaco"),
      e("♐", "signo de sagitário", "zodiaco"),
      e("♑", "signo de capricórnio", "zodiaco"),
      e("♒", "signo de aquário", "zodiaco"),
      e("♓", "signo de peixes", "zodiaco"),
    ],
  },
];

type IndexedEntry = EmojiEntry & { busca: string };

/** Tira acentos e caixa para a busca casar com o que o membro digita. */
function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

const ALL: IndexedEntry[] = CATEGORIES.flatMap(categoria =>
  categoria.emojis.map(item => ({ ...item, busca: fold(`${item.name} ${item.keys}`) })),
);

const BY_CHAR = new Map<string, IndexedEntry>(ALL.map(item => [item.char, item]));

const RECENTS_KEY = "lfrcord.emoji.recentes";
const MAX_RECENTS = 24;
const MAX_RESULTS = 96;
const COLUMNS = 8;

function loadRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string").slice(0, MAX_RECENTS);
  } catch {
    // Navegador em modo privado pode recusar o armazenamento — seguir sem recentes.
    return [];
  }
}

function saveRecents(list: string[]): void {
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch {
    // Sem armazenamento, os recentes valem so para esta sessao.
  }
}

const MONO_LABEL = {
  fontFamily: FONT_MONO,
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: "0.12em",
  color: C.textLabel,
} as const;

export function EmojiPicker({ onPick, onClose, anchor = "acima" }: EmojiPickerProps): ReactElement {
  const [recents, setRecents] = useState<string[]>(loadRecents);
  const [categoria, setCategoria] = useState<CategoriaId>(() =>
    loadRecents().length > 0 ? "recentes" : "rostos",
  );
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);

  const termo = query.trim();

  const lista = useMemo<IndexedEntry[]>(() => {
    const alvo = fold(termo);
    if (alvo.length > 0) {
      const termos = alvo.split(/\s+/);
      const vistos = new Set<string>();
      const achados: IndexedEntry[] = [];
      for (const item of ALL) {
        if (vistos.has(item.char)) continue;
        if (!termos.every(parte => item.busca.includes(parte))) continue;
        vistos.add(item.char);
        achados.push(item);
        if (achados.length >= MAX_RESULTS) break;
      }
      return achados;
    }
    if (categoria === "recentes") {
      const achados: IndexedEntry[] = [];
      for (const char of recents) {
        const item = BY_CHAR.get(char);
        if (item) achados.push(item);
      }
      return achados;
    }
    const grupo = CATEGORIES.find(item => item.id === categoria);
    if (!grupo) return [];
    return grupo.emojis.map(item => ({ ...item, busca: fold(`${item.name} ${item.keys}`) }));
  }, [termo, categoria, recents]);

  // O foco vai para a busca: quem abriu o seletor quer filtrar ou escolher.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActive(0);
  }, [termo, categoria]);

  // Escape fecha aqui e nao vaza para os atalhos globais do app.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const box = boxRef.current;
      if (box && event.target instanceof Node && box.contains(event.target)) return;
      onClose();
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [onClose]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [active, lista]);

  const pick = useCallback(
    (char: string) => {
      setRecents(anterior => {
        const proximo = [char, ...anterior.filter(item => item !== char)].slice(0, MAX_RECENTS);
        saveRecents(proximo);
        return proximo;
      });
      onPick(char);
    },
    [onPick],
  );

  const onSearchKey = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (lista.length === 0) return;
      const passo = (delta: number) => {
        event.preventDefault();
        setActive(indice => {
          const proximo = indice + delta;
          if (proximo < 0) return 0;
          if (proximo > lista.length - 1) return lista.length - 1;
          return proximo;
        });
      };
      if (event.key === "ArrowRight") passo(1);
      else if (event.key === "ArrowLeft") passo(-1);
      else if (event.key === "ArrowDown") passo(COLUMNS);
      else if (event.key === "ArrowUp") passo(-COLUMNS);
      else if (event.key === "Enter") {
        event.preventDefault();
        const escolhido = lista[Math.min(active, lista.length - 1)];
        if (escolhido) pick(escolhido.char);
      }
    },
    [active, lista, pick],
  );

  const destaque = lista[Math.min(active, Math.max(lista.length - 1, 0))];
  const tituloLista =
    termo.length > 0
      ? `RESULTADOS — ${lista.length}`
      : categoria === "recentes"
        ? "USADOS RECENTEMENTE"
        : (CATEGORIES.find(item => item.id === categoria)?.label ?? "").toUpperCase();

  const abas: { id: CategoriaId; glyph: string; label: string }[] = [
    ...(recents.length > 0
      ? [{ id: "recentes" as CategoriaId, glyph: "🕘", label: "Usados recentemente" }]
      : []),
    ...CATEGORIES.map(item => ({ id: item.id as CategoriaId, glyph: item.glyph, label: item.label })),
  ];

  return (
    <div
      ref={boxRef}
      role="dialog"
      aria-label="Seletor de emoji"
      style={{
        position: "absolute",
        right: 0,
        [anchor === "acima" ? "bottom" : "top"]: "calc(100% + 8px)",
        width: 316,
        border: `1px solid ${C.lineStrong}`,
        borderRadius: 12,
        overflow: "hidden",
        background: C.raised,
        boxShadow: "0 16px 40px rgba(0,0,0,.5)",
        zIndex: 30,
        animation: "pop 120ms ease",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 2,
          padding: "6px 6px 5px",
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        {abas.map(aba => {
          const atual = aba.id === categoria && termo.length === 0;
          return (
            <button
              key={aba.id}
              type="button"
              className="hov-bg-24"
              title={aba.label}
              aria-label={aba.label}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                setQuery("");
                setCategoria(aba.id);
              }}
              style={{
                flex: 1,
                height: 28,
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 8,
                cursor: "pointer",
                background: atual ? C.selected : "transparent",
                fontSize: 15,
                lineHeight: 1,
                opacity: atual ? 1 : 0.68,
                transition: "background 130ms ease,opacity 130ms ease",
              }}
            >
              {aba.glyph}
            </button>
          );
        })}
      </div>

      <div style={{ padding: "8px 8px 4px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 10px",
            border: `1px solid ${C.lineStrong}`,
            borderRadius: 9,
            background: C.field,
          }}
        >
          <span style={{ color: C.textFaint, display: "grid", placeItems: "center" }}>
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.7}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="6.5" />
              <path d="M15.8 15.8 20 20" />
            </svg>
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Buscar emoji"
            aria-label="Buscar emoji pelo nome"
            style={{
              flex: 1,
              minWidth: 0,
              border: 0,
              outline: 0,
              background: "transparent",
              color: C.text,
              fontSize: 13,
              padding: "8px 0",
            }}
          />
        </div>
      </div>

      <div style={{ ...MONO_LABEL, padding: "6px 12px 4px" }}>{tituloLista}</div>

      <div style={{ maxHeight: 208, overflowY: "auto", padding: "0 6px 6px" }}>
        {lista.length === 0 ? (
          <div style={{ padding: "14px 8px 18px", fontSize: 12.5, color: C.textDim }}>
            {termo.length > 0
              ? `Nenhum emoji com “${termo}”. Tente “fogo”, “coração” ou “festa”.`
              : "Escolha um emoji para ele aparecer aqui da próxima vez."}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${COLUMNS}, 1fr)`,
              gap: 2,
            }}
          >
            {lista.map((item, indice) => {
              const atual = indice === active;
              return (
                <button
                  key={`${item.char}-${indice}`}
                  ref={atual ? activeRef : null}
                  type="button"
                  className="hov-bg-24"
                  title={item.name}
                  aria-label={item.name}
                  onMouseDown={event => event.preventDefault()}
                  onMouseEnter={() => setActive(indice)}
                  onClick={() => pick(item.char)}
                  style={{
                    height: 34,
                    display: "grid",
                    placeItems: "center",
                    border: 0,
                    borderRadius: 8,
                    cursor: "pointer",
                    background: atual ? C.selected : "transparent",
                    fontSize: 19,
                    lineHeight: 1,
                  }}
                >
                  {item.char}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 12px",
          borderTop: `1px solid ${C.line}`,
          background: C.stage,
        }}
      >
        <span style={{ fontSize: 15, lineHeight: 1 }}>{destaque ? destaque.char : "🙂"}</span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: FONT_MONO,
            fontSize: 9,
            letterSpacing: "0.08em",
            color: C.textLabel,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {destaque ? destaque.name.toUpperCase() : "ESCOLHA UM EMOJI"}
        </span>
        <span
          style={{
            flex: "0 0 auto",
            fontFamily: FONT_MONO,
            fontSize: 9,
            letterSpacing: "0.1em",
            color: C.textGhost,
          }}
        >
          ESC FECHA
        </span>
      </div>
    </div>
  );
}
